import { ThunkDispatch } from "redux-thunk";
import RootState from "store/state";

import * as QaApi from "services/api/api.qa";

import * as CompanyApi from 'services/api/api.company'

import { 
    IScoreSearch,
    IScoreDetails,
    IScore,
    ICalibration,
    ICalibrationFull,
    CalibrationStatus,
    IQATemplate,
    EvaluationConfig,
} from "./qa.state";
import { QATypes } from "./qa.types";
import { CTR } from "store/contact/contact.state";
import { IScoreSearchForm } from "views/QA/ScoreSearch/ScoreSearch.types";
import { AxiosError } from "axios";
import { logout } from "store/user/user.actions";
import { setRedirect } from "store/global/global.actions";
import { TemplateForm_Form } from "views/QA/Templates/TemplateForm/TemplateForm.types";
import { createNotification } from "store/notification/notification.actions";
import AnyAction from "store/anyActions";

export type QAAction =
    | { type: typeof QATypes.SEARCH_SCORES, payload: IScoreSearch[] }
    | { type: typeof QATypes.SEARCH_SCORES_LOADING }
    | { type: typeof QATypes.SEARCH_SCORES_ERROR }
    | { type: typeof QATypes.SEARCH_CALIBRATIONS; payload: ICalibration[] }
    | { type: typeof QATypes.SEARCH_CALIBRATIONS_LOADING }
    | { type: typeof QATypes.SEARCH_CALIBRATIONS_ERROR }
    | { type: typeof QATypes.SET_CURRENTLY_CALIBRATING; payload: { score: IScore } }
    | { type: typeof QATypes.GET_FULL_CALIBRATION; payload: ICalibrationFull }
    | { type: typeof QATypes.CLEAR_CALIBRATION; }
    | { type: typeof QATypes.CALIBRATION_BUSY; payload: string | undefined }

    | { type: typeof QATypes.GET_SCORE; payload: IScoreDetails }
    | { type: typeof QATypes.GET_SCORE_LOADING; payload: string }
    | { type: typeof QATypes.CLEAR_SCORE; }
    | { type: typeof QATypes.UPDATE_SCORE; payload: IScoreDetails }
    | { type: typeof QATypes.UPDATE_SCORE_LOADING; }
    | { type: typeof QATypes.SCORE_CALCULATE; payload: boolean }
    | { type: typeof QATypes.NEW_SCORE; payload: INewScore }
    | { type: typeof QATypes.DELETE_SCORE_LOADING }
    | { type: typeof QATypes.DELETING_SCORE, payload: string }
    | { type: typeof QATypes.DELETE_SCORE, payload: string }
    | { type: typeof QATypes.SET_SCORE_SEARCH_PARAMS, payload: IScoreSearchForm }
    | { type: typeof QATypes.SET_SHOW_NEW_SCORE, payload: boolean }
    | { type: typeof QATypes.START_CALIBRATION, payload: { scoreID: string, evaluatorID: string } }
    | { type: typeof QATypes.CHANGE_CALIBRATION_STATUS; payload: ICalibration }
    | { type: typeof QATypes.GET_MY_SCORES, payload: IScoreSearch[] }
    | { type: typeof QATypes.GET_QA_TEMPLATES, payload: IQATemplate[] }
    | { type: typeof QATypes.CREATE_QA_TEMPLATE }
    | { type: typeof QATypes.EDIT_QA_TEMPLATE }
    | { type: typeof QATypes.DELETE_QA_TEMPLATE }
    | { type: typeof QATypes.MY_SCORES_LOADING }
    | { type: typeof QATypes.MY_SCORES_ERROR }
    | { type: typeof QATypes.CREATING_SCORE }
    | { type: typeof QATypes.GET_EVALUATION_CONFIG_LOADING, payload: { loading: boolean }}
    | { type: typeof QATypes.GET_EVALUATION_CONFIG, payload: EvaluationConfig }
    | { type: typeof QATypes.GET_EVALUATION_CONFIG_ERROR, payload: { error: boolean } }
    | { type: typeof QATypes.GET_UNREAD_SCORES_COUNT, payload: { unreadScoresCount: number } }
    | { type: typeof QATypes.SET_UNREAD_SCORES_COUNT, payload: { unreadScoresCount: number } }


function calibrationBusy(calibrationID: string | undefined) : QAAction {
    return {
        type: QATypes.CALIBRATION_BUSY,
        payload: calibrationID
    }
}

export function clearCalibration() {
    return {
        type: QATypes.CLEAR_CALIBRATION,
    }
}

export function changeCalibrationStatus(calibrationID: string, status: CalibrationStatus) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {
        try {
            dispatch(calibrationBusy(calibrationID))
            const companyID = getState().app.ID;
            const token = getState().auth.token!;
    
            const response = await QaApi.putCalibrationStatus(companyID, token, calibrationID, status);
    
            dispatch({
                type: QATypes.CHANGE_CALIBRATION_STATUS,
                payload: response.data
            })

        } catch (err) {
            console.log("error changing calibration status", err)
        }
    }
}

export function getFullCalibration(calibrationID: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {
        if (getState().qa.currentCalibration?.ID === calibrationID) return;

        try {
            dispatch(calibrationBusy(calibrationID))
            const companyID = getState().app.ID;
            const token = getState().auth.token!;
    
            const response = await QaApi.getFullCalibration(companyID, token, calibrationID);
    
            dispatch({
                type: QATypes.GET_FULL_CALIBRATION,
                payload: response.data
            })

        } catch (err) {
            console.log("error getting full calibration object", err)
        }

    }

}

export function postNewCalibrationScore(calibrationID: string, redirect?: boolean) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, any>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;

        dispatch(calibrationBusy(calibrationID));

        const response = await QaApi.postCalibrationScore(companyID, token, calibrationID)
        
        dispatch({
            type: QATypes.GET_SCORE,
            payload: response.data
        });

        dispatch(calibrationBusy(undefined));

        if(redirect) dispatch(setRedirect(`/qa/scores/${response.data.ID}`))

    }

}

export function searchCalibrationsLoading(): QAAction {
    return {
        type: QATypes.SEARCH_CALIBRATIONS_LOADING
    }
}

export function searchCalibrationsError(): QAAction {
    return {
        type: QATypes.SEARCH_CALIBRATIONS_ERROR
    }
}

export function searchCalibrations(queryString: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {

        dispatch(searchCalibrationsLoading())

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;

            const response = await QaApi.getCalibrations(companyID, token, queryString);
            dispatch({
                type: QATypes.SEARCH_CALIBRATIONS,
                payload: response.data.data
            })


        } catch (err) {

            if (err.response?.status === 403) return dispatch(logout())
            else dispatch(searchCalibrationsError())

        }
    }
}


export function startNewCalibration(scoreID: string, evaluatorID: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;

            await QaApi.startCalibration(companyID, token, scoreID, evaluatorID)

            dispatch({
                type: QATypes.START_CALIBRATION,
                payload: {
                    scoreID,
                    evaluatorID
                }
            })

        } catch (err) {
            console.log("error creating new calibration", err)
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())
        }
    }
}

export function setShowNewQAScore(show: boolean): QAAction {
    return {
        type: QATypes.SET_SHOW_NEW_SCORE,
        payload: show
    }
}

export function setScoreSearchParams(formState: IScoreSearchForm): QAAction {
    return {
        type: QATypes.SET_SCORE_SEARCH_PARAMS,
        payload: formState
    }
}

export function clearScore(): QAAction {
    return {
        type: QATypes.CLEAR_SCORE
    }
}

export function setScoreCalculating(calculating = true): QAAction {
    return {
        type: QATypes.SCORE_CALCULATE,
        payload: calculating
    }
}

function updateScoreLoading(): QAAction {
    return {
        type: QATypes.UPDATE_SCORE_LOADING
    }
}

interface INewScore {
    contact?: CTR;
    newScore: IScoreDetails
}
export function newScore(score: IScore, contact?: CTR, redirect?:boolean) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, any>,
        getState: () => RootState
    ) => {
        dispatch(updateScoreLoading())

        if(!score.instanceID){
            score.instanceID = getState().app.instance?.ID;
        }

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;

            dispatch({
                type: QATypes.CREATING_SCORE,
            })

            const newScore = await QaApi.postScore(companyID, token, score)

            dispatch({
                type: QATypes.NEW_SCORE,
                payload: {
                    contact,
                    newScore: newScore.data
                }
            })

            if(redirect) dispatch(setRedirect(`/qa/scores/${newScore.data.ID}`))

        } catch (err) {
            console.log("error updating score", err)
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

        }

    }
}

export function updateScore(score: IScore) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {
        dispatch(updateScoreLoading())

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;


            const updatedScore =
                await QaApi.putScore(companyID, token, score)


            dispatch({
                type: QATypes.UPDATE_SCORE,
                payload: updatedScore.data
            })

        } catch (err) {
            console.log("error updating score", err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

        }
    }
}

export function deleteScore(scoreID: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
        getState: () => RootState
    ) => {

        dispatch(deleteScoreLoading())

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;

            await QaApi.deleteScore(companyID, token, scoreID);

            dispatch({
                type: QATypes.DELETE_SCORE,
                payload: scoreID
            })

            dispatch(createNotification({
                type: "success",
                header: "Evaluation Deleted",
                text: `Evaluation was deleted successfully`
            }))

        } catch (err) {
            console.log("error getting score", err)
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

        }
    }

}

export function getMyScores() {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {
        dispatch(myScoresLoading())

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;
            const agentID = getState().user.agentID;
            const scores = await QaApi.searchScores(companyID, token, `agentID=${agentID}&status=!IN_PROGRESS`);

            return dispatch({
                type: QATypes.GET_MY_SCORES,
                payload: scores.data.data
            })

        } catch (err) {
            console.log("error getting me scores", err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            dispatch(myScoresError())
        }
    }

}


export function searchScores(queryString: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {
        dispatch(searchScoresLoading())

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;
            const scores = await QaApi.searchScores(companyID, token, queryString);

            return dispatch({
                type: QATypes.SEARCH_SCORES,
                payload: scores.data.data
            })

        } catch (err) {
            console.log("error searching scores", err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            dispatch(searchScoresError())
        }
    }

}

function getScoreLoading(scoreID: string): QAAction {
    return {
        type: QATypes.GET_SCORE_LOADING,
        payload: scoreID
    }
}

export function setCurrentScore(score: IScoreDetails) : QAAction {
    return {
        type: QATypes.GET_SCORE,
        payload: score
    }

}

export function getScore(scoreID: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {

        dispatch(getScoreLoading(scoreID));

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;

            const score = await QaApi.getScore(companyID, token, scoreID);

            dispatch(setCurrentScore(score.data))

        } catch (err) {
            console.log("error getting score", err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

        }

    }
}

export function getQATemplateList(){
    return async (
        dispatch: ThunkDispatch<RootState, undefined, QAAction>,
        getState: () => RootState
    ) => {
        try{
            const companyID = getState().app.ID;
            const token = getState().auth.token!;
            const response = await CompanyApi.getFormTemplateList(companyID,token);
            dispatch({
                type: QATypes.GET_QA_TEMPLATES,
                payload: response.data
            })
        }catch(ex){
            console.log('error getting qa template list',ex)
        }
    }
}

export function createQATemplate(template:TemplateForm_Form){
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
        getState: () => RootState
    ) => {
        try{
            const companyID = getState().app.ID;
            const token = getState().auth.token!;
            await CompanyApi.createFormTemplate(companyID, template, token);
            dispatch({
                type: QATypes.CREATE_QA_TEMPLATE
            });

            dispatch(createNotification({
                type: "success",
                header: "Template Saved",
                text: `Template: ${template.name} was saved successfully`
            }))

        }catch(ex){
            console.log('error creating qa template',ex)
        }
    }
}

export function editQATemplate(template:TemplateForm_Form){
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
        getState: () => RootState
    ) => {
        try{
            const companyID = getState().app.ID;
            const token = getState().auth.token!;
            const templateID = template.ID!;
            templateID && delete template.ID;
            await CompanyApi.editFormTemplate(companyID, templateID, template, token);
            dispatch({
                type: QATypes.EDIT_QA_TEMPLATE
            })
            dispatch(createNotification({
                type: "success",
                header: "Template Saved",
                text: `Template: ${template.name} was saved successfully`
            }))
            
        }catch(ex){
            console.log('error editing qa template',ex)
        }
    }
}

export function deleteQATemplate(formID:string){
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
        getState: () => RootState
    ) => {
        try{
            const companyID = getState().app.ID;
            const token = getState().auth.token!;
            await CompanyApi.deleteFormTemplate(companyID,formID,token);

            dispatch({
                type: QATypes.DELETE_QA_TEMPLATE
            })

            dispatch(createNotification({
                type: "success",
                header: "Template Deleted",
                text: `Template deleted successfully`
            }))
            
        }catch(ex){
            console.log('error deleting qa template',ex)
        }
    }
}

function searchScoresLoading(): QAAction {
    return {
        type: QATypes.SEARCH_SCORES_LOADING
    }
}

function deleteScoreLoading(): QAAction {
    return {
        type: QATypes.DELETE_SCORE_LOADING,
    }
}

export function setDeletingScore(scoreID: string): QAAction {
    return {
        type: QATypes.DELETING_SCORE,
        payload: scoreID
    }
}


function searchScoresError(): QAAction {
    return {
        type: QATypes.SEARCH_SCORES_ERROR
    }
}

function myScoresError(): QAAction {
    return {
        type: QATypes.MY_SCORES_ERROR
    }
}

function myScoresLoading(): QAAction {
    return {
        type: QATypes.MY_SCORES_LOADING
    }
}

export function getEvaluationConfigLoading(loading: boolean): QAAction {
    return {
        type: QATypes.GET_EVALUATION_CONFIG_LOADING,
        payload: {
            loading,
        },
    };
}

export function getEvaluationConfig() {
    return async (
        dispatch: ThunkDispatch<RootState, void, QAAction>,
        getState: () => RootState
    ) => {
        const { app, auth } = getState();
        const companyID = app.ID;
        const token = auth.token!;

        dispatch(getEvaluationConfigLoading(true));
        try {
            const evaluationConfig = await QaApi.getEvaluationConfig(companyID, token);
            dispatch({
                type: QATypes.GET_EVALUATION_CONFIG,
                payload: evaluationConfig,
            });
        } catch (err) {
            console.error('Error getting evaluation config:', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout());

            dispatch(
                getEvaluationConfigError(true)
            );
        }
    };
}

export function getEvaluationConfigError(error: boolean): QAAction {
    return {
        type: QATypes.GET_EVALUATION_CONFIG_ERROR,
        payload: { error },
    };
}

export function setUnreadScoresCount(count: number) : QAAction {
    return {
        type: QATypes.SET_UNREAD_SCORES_COUNT,
        payload: {
            unreadScoresCount: count,
        }
    }
}

export function getUnreadScoresCount() {
    return async (
        dispatch: ThunkDispatch<RootState, void, QAAction>,
        getState: () => RootState
    ) => {
        const { auth, app } = getState();
        const companyID = app.ID;
        const token = auth.token!;

        try {
            const response = await QaApi.getUnreadScoresCount(companyID, token);
            response !== undefined && dispatch(setUnreadScoresCount(response.data));
        } catch (error) {
            console.error('Error getting unread scores count : ', error);
        }
    }
}
